import type { CSSProperties, HTMLAttributes } from "react";
import optics from "../../../surfacekit/assets/rumonai/core-icon-optics.json";
import photoMask from "../../../surfacekit/assets/rumonai/icons/image.svg?url";

/** The complete original artwork family, available to declared surfaces too. */
export const rumonaiIconNames = [
  "core-work", "core-research", "core-plan", "core-dialogue", "core-edit", "core-reference",
  "play", "pause", "stop", "magnifying-glass", "list-bullets", "chat-circle", "pencil-simple", "link",
  "plus", "minus", "x", "check", "caret-down", "caret-right", "caret-left", "caret-up",
  "arrow-right", "arrow-left", "arrow-up", "arrow-down", "upload-simple", "download-simple", "copy", "trash",
  "floppy-disk", "folder", "folder-open", "file-text", "photo", "film-strip", "music-note", "microphone",
  "bell", "gear-six", "user", "users", "calendar-blank", "clock", "eye", "eye-slash",
  "lock-key", "key", "warning-circle", "info", "check-circle", "spinner-gap", "sun", "moon",
  "heart", "star", "funnel", "arrows-out", "arrow-counter-clockwise", "dots-three",
] as const;

export type RumonaiIconName = typeof rumonaiIconNames[number];

// Pages retain their semantic interface; every mark comes from the same artwork.
const semanticIcons = {
  back: "arrow-left",
  arrow: "arrow-right",
  refresh: "arrow-counter-clockwise",
  today: "core-plan",
  identity: "user",
  content: "core-work",
  channels: "link",
  waiting: "bell",
  yes: "check",
  no: "x",
  up: "caret-up",
  down: "caret-down",
  shield: "lock-key",
  money: "star",
  person: "user",
  agent: "core-work",
  people: "users",
  send: "arrow-up",
  volume: "music-note",
  muted: "pause",
  like: "heart",
  liked: "heart",
  calendar: "calendar-blank",
  clapperboard: "film-strip",
  picture: "photo",
  sound: "music-note",
  file: "file-text",
  attach: "upload-simple",
  "list-check": "list-bullets",
  "pencil-ruler": "core-edit",
  youtube: "play",
  "eye-off": "eye-slash",
  tool: "gear-six",
  alert: "warning-circle",
  done: "check-circle",
  developer: "gear-six",
  look: "eye",
  wall: "folder-open",
  chat: "core-dialogue",
  settings: "gear-six",
  brain: "core-research",
  memory: "core-reference",
  chart: "core-research",
  code: "file-text",
  search: "magnifying-glass",
  theme: "sun",
  studio: "core-work",
  inbox: "folder-open",
  logout: "arrow-right",
  more: "dots-three",
  expand: "arrows-out",
  collapse: "caret-left",
  question: "info",
  edit: "core-edit",
} as const satisfies Record<string, RumonaiIconName>;

export type IconName = RumonaiIconName | keyof typeof semanticIcons;
const names = new Set<string>([...rumonaiIconNames, ...Object.keys(semanticIcons)]);

/** Accept only names supplied by the shared artwork family. */
export function declaredIcon(value: unknown, fallback: IconName = "studio"): IconName {
  return typeof value === "string" && names.has(value) ? value as IconName : fallback;
}

export type IconProps = HTMLAttributes<HTMLSpanElement> & {
  name: IconName;
  size?: number | string;
  mono?: boolean;
};

export function Icon({ name, size = 18, mono = false, className = "", style, ...props }: IconProps) {
  const glyph: RumonaiIconName = Object.hasOwn(semanticIcons, name)
    ? semanticIcons[name as keyof typeof semanticIcons] : name as RumonaiIconName;
  const dimensions = { "--icon-size": typeof size === "number" ? `${size}px` : size };
  if (glyph.startsWith("core-")) {
    const key = glyph.slice(5) as keyof typeof optics;
    const optical = optics[key];
    return <span {...props} aria-hidden="true" data-icon={name} data-glyph={glyph}
      className={`icon core-icon ${className}`.trim()}
      style={{ ...dimensions, "--optical-scale": optical.scale, "--optical-x": `${optical.x}%`, "--optical-y": `${optical.y}%`, ...style } as CSSProperties}>
      <img src={new URL(`../../../surfacekit/assets/rumonai/core-icons/${key}.webp`, import.meta.url).href} alt="" draggable={false} />
    </span>;
  }
  const mask = glyph === "photo" ? photoMask
    : new URL(`../../../surfacekit/assets/rumonai/icons/${glyph}.svg`, import.meta.url).href;
  return <span {...props} aria-hidden="true" data-icon={name} data-glyph={glyph}
    className={`icon paint-icon${mono ? " mono" : ""} ${className}`.trim()}
    style={{ ...dimensions, "--icon-mask": `url("${mask}")`, ...style } as CSSProperties} />;
}
